import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";

import {Add} from '@mui/icons-material';
import COLOR from "utils/constants/color";
import {Cancel} from '@mui/icons-material';
import {Check, Share} from '@mui/icons-material';
import styled from "styled-components";
import { styled as muiStyled } from '@mui/material/styles';
import { Row as Rows } from "antd";
import { WriteEditWhite } from "utils/assets/images";
import { isEmpty, map } from "lodash";
import { v4 as uuid } from "uuid";
import { useDrag, useDrop } from "react-dnd";
import { confirmUnshareWidget } from "../../../store/widgets/confirmUnshare";
import {
  Button,
  IconButton,
  TextField
} from '@mui/material';
import {  makeStyles } from '@mui/styles';
// Define custom styles using makeStyles
const useStyles = makeStyles({
  rows: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: "5px",
    marginTop: "52px",
  },
  inputField: (windowSize) => ({
    width: windowSize?.width >= 768 ? 124 : 46,
    color: "#000",
  }),
  inputLabelProps: {
    color: "#000",
    fontSize: "8px",
  },
  editListOptionSmall: {
    width: "76px",
  },
  resetButtonSmall: {
    width: "47px",
  },
});

const isTouchDevice = () => {
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

const UserInputSection = ({ groupIndex, newUser, onUserChange, onAddUser, onToggleInput }) => {
  const firstNameValue = newUser?.firstName || "";
  const lastNameValue = newUser?.lastName || "";
  
  return (
    <div>
      <UserInput
        id={`firstName-${groupIndex}`}
        type="text"
        name="firstName"
        placeholder="First Name"
        className="first-name-color"
        variant="outlined"
        size="small"
        style={{ color: "white" }}
        InputLabelProps={{ shrink: true }}
        inputProps={{ shrink: true }}
        value={firstNameValue}
        onChange={(e) => onUserChange(e, groupIndex)}
      />
      <UserInput
        id={`lastName-${groupIndex}`}
        type="text"
        name="lastName"
        placeholder="Last Name"
        className="first-name-color"
        variant="outlined"
        size="small"
        style={{ color: "white", marginTop: "10px" }}
        InputLabelProps={{ shrink: true }}
        inputProps={{ shrink: true }}
        value={lastNameValue}
        onChange={(e) => onUserChange(e, groupIndex)}
      />
      <Button
        className="checkRghtBtn"
        variant="contained"
        color="primary"
        size="small"
        style={{ marginTop: "10px", borderRadius: "50%" }}
        startIcon={<Check />}
        onClick={async () => {
          await onAddUser(groupIndex);
          onToggleInput(groupIndex);
        }}
      />
    </div>
  );
};

const DroppableGroupArea = React.memo(({ groupIndex, children, onDrop, expandedGroup }) => {
  const [{ isOver }, drop] = useDrop({
    accept: 'USER',
    drop: (item) => {
      if (item.groupIndex !== groupIndex) {
        onDrop(item, groupIndex);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <UserListArea
      ref={drop}
      style={{ 
        width: "200px",
        border: isOver ? "2px dashed #4CAF50" : "none",
        backgroundColor: isOver ? "rgba(76, 175, 80, 0.1)" : "transparent",
      }}
      className={
        expandedGroup === groupIndex ? "expandOnDragOver" : ""
      }
    >
      {children}
    </UserListArea>
  );
});

const WidgetGroupComponent = (props) => {
  const isTouch = useMemo(() => isTouchDevice(), []);
  const { shareStatus, perGroupStudent, generatedGroup, currentUser } =
    props?.states || {};
  const {
    handleOpenEditModal,
    onClose,
    onChangePerStation,
    handlerGenerateStudentGroup,
    handleUserNewAdded,
    resetGroup,
    handlerShareGroup,
    updateGeneratedGroup,
  } = props?.funcObj || {};

  const userAreaRef = useRef(null);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const classes = useStyles(windowSize);

  const [dragging, setDragging] = useState(null);
  const [newUser, setNewUser] = useState({});
  const [showInput, setShowInput] = useState({});
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    setGroups(generatedGroup);
  }, [generatedGroup]);

  const handleResize = () => {
    setWindowSize({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleScroll = (event) => {
    const { clientY } = event;
    const userArea = userAreaRef.current;

    if (!userArea) return;

    const { top, bottom } = userArea.getBoundingClientRect();
    const offset = 10;

    if (clientY - top < offset) {
      userArea.scrollTop -= offset;
    } else if (bottom - clientY < offset) {
      userArea.scrollTop += offset;
    }
  };

  const toggleInput = useCallback((groupIndex) => {
    setShowInput((prev) => {
      const isOpening = !prev[groupIndex];
      if (isOpening) {
        setNewUser((prevUser) => ({
          ...prevUser,
          [groupIndex]: { firstName: "", lastName: "" },
        }));
      }
      return { ...prev, [groupIndex]: isOpening };
    });
  }, []);

  const handleNewUserChange = useCallback((e, groupIndex) => {
    const fieldName = e.target.name;
    const fieldValue = e.target.value;
    setNewUser((prev) => ({
      ...prev,
      [groupIndex]: { 
        ...(prev[groupIndex] || {}), 
        [fieldName]: fieldValue 
      },
    }));
  }, []);

  const handleAddNewUserToGroup = useCallback(async (groupIndex) => {
    setNewUser((prevNewUser) => {
      if (prevNewUser[groupIndex]) {
        setGroups((prevGroups) => {
          const updatedGroups = prevGroups.map((group, index) => {
            if (index === groupIndex) {
              const userPayload = {
                id: uuid(),
                role: "student",
                userName: `${prevNewUser[groupIndex]?.firstName || ""} ${
                  prevNewUser[groupIndex]?.lastName || ""
                }`,
              };
              return [...group, userPayload];
            }
            return group;
          });
          handleUserNewAdded(updatedGroups, prevNewUser[groupIndex]);
          return updatedGroups;
        });
        return { ...prevNewUser, [groupIndex]: null };
      }
      return prevNewUser;
    });
  }, [handleUserNewAdded]);



  const handleDrop = useCallback(async (item, groupIndex) => {
    try { 
      if (item.groupIndex !== groupIndex) {
        setGroups((prevGroups) => {
          const sourceGroup = prevGroups[item.groupIndex].filter(
            (user) => user.id !== item.userId
          );
          const droppedUser = prevGroups[item.groupIndex].find(
            (user) => user.id === item.userId
          );
          
          if (droppedUser) {
            const targetGroup = [...prevGroups[groupIndex], droppedUser];
            const newGroups = prevGroups.map((group, index) => {
              if (index === item.groupIndex) return sourceGroup;
              if (index === groupIndex) return targetGroup;
              return group;
            });
            updateGeneratedGroup(newGroups);
            return newGroups;
          }
          return prevGroups;
        });
      }
    } catch (error) {
      console.error("Error in handleDrop:", error);
    }
    setExpandedGroup(null);
    setDragging(null);
  }, [updateGeneratedGroup]);

  const handleCloseClick = async () => {
    const shouldClose = await confirmUnshareWidget({
      isShared: shareStatus,
      widgetName: "Group widget",
    });
    if (!shouldClose) return;
    onClose();
  };

  // Draggable User Item Component
  const DraggableUserItem = ({ user, groupIndex, currentUser }) => {
    const [{ isDragging }, drag] = useDrag({
      type: 'USER',
      item: { userId: user.id, groupIndex },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        if (!monitor.didDrop()) {
          setDragging(null);
        }
      },
    });

    const MyUser = user?.id === currentUser?.id;

    return (
      <UserItem
        ref={drag}
        getMyUser={MyUser}
        style={{ 
          cursor: "grab",
          opacity: isDragging ? 0.5 : 1,
        }}
      >
        {user?.userName}
      </UserItem>
    );
  };

  return (
      <div className="custom-fullscreen-container newStationModal">
      <div className="modal-container-parent">
        <div className="modal-container-new-station addWidgetSpc">
          <div style={{ marginTop: "15px" }} className="heading-label">
            New Group
          </div>
          <div className="modal-inner-container">
            <button
              style={{
                backgroundColor: shareStatus && "green",
              }}
              onClick={handlerShareGroup}
              className="share-button-widget-modal "
            >
              <Share style={{ color: "white", fontSize: 13 }} />
              {!shareStatus ? "Share" : "Shared"}
            </button>
            <div onClick={handleCloseClick} className="cancel-icon-station">
              <Cancel />
            </div>
          </div>

        <Rows className={classes.rows}>
          <InputField
            id="noOfGroup"
            name="noOfGroup"
            label={windowSize?.width >= 768 ? "Students per group" : ""}
            InputLabelProps={{
              shrink: true,
              required: true,
              style: { color: "#000" },
              fontSize: "8px",
            }}
            inputProps={{
              shrink: true,
              required: true,
              style: { color: "#000" },
              fontSize: "8px",
            }}
            type="number"
            variant="outlined"
            onChange={(e) => {
              if (e.target.value >= 0) {
                onChangePerStation(e.target.value);
              }
            }}
            value={perGroupStudent}
            className={`${classes.inputField} group-input-label`}
          />
          <GenerateListButton onClick={() => handlerGenerateStudentGroup()}>
            Generate
          </GenerateListButton>
          <ResetButton onClick={resetGroup}>Reset</ResetButton>
          <button
            onClick={() => handleOpenEditModal(true)}
            style={{ border: "none", background: "none", outline: "none" }}
          >
            <EditListOption>
              Edit <IconsImg src={WriteEditWhite} />
            </EditListOption>
          </button>
        </Rows>

        <UserArea ref={userAreaRef} onDragOver={handleScroll}>
          {isEmpty(groups) ? (
            <UserListAreaEmpty
              className="gradient-blue-pink"
              style={{
                justifyContent: "center",
                width: 654,
                height: 304,
              }}
            >
              <span style={{ color: "white", fontSize: 18 }}>
                Not available.
              </span>
            </UserListAreaEmpty>
          ) : (
            <>
              {map(groups, (item, groupIndex) => (
                <DroppableGroupArea 
                  key={groupIndex} 
                  groupIndex={groupIndex}
                  onDrop={handleDrop}
                  expandedGroup={expandedGroup}
                >
                  {map(item, (user) => (
                    <DraggableUserItem key={user.id} user={user} groupIndex={groupIndex} currentUser={currentUser} />
                  ))}
                  {!showInput[groupIndex] && (
                    <IconButton
                      color="primary"
                      onClick={() => toggleInput(groupIndex)}
                    >
                      <Add />
                    </IconButton>
                  )}
                  {showInput[groupIndex] && (
                    <UserInputSection
                      key={`input-section-${groupIndex}`}
                      groupIndex={groupIndex}
                      newUser={newUser[groupIndex]}
                      onUserChange={handleNewUserChange}
                      onAddUser={handleAddNewUserToGroup}
                      onToggleInput={toggleInput}
                    />
                  )}
                </DroppableGroupArea>
              ))}
            </>
          )}
        </UserArea>
      </div>
      <div className="widgetFooterNewStation NewGrpFtr"><label>Pop Up</label></div>
      </div>
    </div>
  );
};

export default WidgetGroupComponent;

const UserArea = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: 16px;
  flex-wrap: wrap;
  max-height: 55vh;
  justify-content: center;
  // max-width: 50vw;
  // @media (max-width: 997px) {
  //   max-width: 90vw;
  // }
  //   overflow: auto !important;
  ::-webkit-scrollbar {
    width: 3px;
    height: 6px;
  }
`;

const UserListAreaEmpty = styled.div`
  background: #cbbbe7;
  min-height: 114px;
  min-width: 175px;
  border-radius: 10px;
  padding: 10px;
  margin: 12px 16px;
  display: flex;
  align-items: center;
`;

const UserListArea = styled.div`
  background: linear-gradient(
    90deg,
    #df6074 0%,
    #bd57b4 30%,
    #a451e7 50%,
    #9a59e8 60%,
    #7579f0 90%,
    #6786f4 100%
  );
  min-height: 114px;
  // width: 200px;
  min-width: 175px;
  border-radius: 10px;
  padding: 10px;
  margin: 12px 16px;
`;

const UserItem = styled.div`
  background: ${(p) => (p.getMyUser ? "#152B69" : "#fff")};
  color: ${(p) => (p.getMyUser ? "#fff" : "#000")};
  padding: 8px 10px;
  border-radius: 8px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: move;
`;

const UserInput = muiStyled(TextField)({
  marginTop: '8px',
});

// const InputField = styled(
//   {
//   root: {
//     "& label.Mui-focused": {
//       color: "white",
//     },
//     "& .MuiInput-underline:after": {
//       borderBottomColor: "white",
//     },
//     "& .MuiOutlinedInput-root": {
//       "& fieldset": {
//         borderColor: "rgba(0, 0, 0, 0.23)",
//       },
//       "&:hover fieldset": {
//         borderColor: "rgba(0, 0, 0, 0.23)",
//       },
//       "&.Mui-focused fieldset": {
//         borderColor: "rgba(0, 0, 0, 0.23)",
//       },
//       "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
//         {
//           "-webkit-appearance": "none",
//           margin: 0,
//         },
//       "& input[type=number]": {
//         "-moz-appearance": "textfield", // For Firefox
//       },
//     },
//   },
// })(TextField);


const InputField = styled(TextField)`
  & label.Mui-focused {
    color: white;
  }

  & .MuiInput-underline:after {
    border-bottom-color: white;
  }

  & .MuiOutlinedInput-root {
    & fieldset {
      border-color: rgba(0, 0, 0, 0.23);
    }

    &:hover fieldset {
      border-color: rgba(0, 0, 0, 0.23);
    }

    &.Mui-focused fieldset {
      border-color: rgba(0, 0, 0, 0.23);
    }

    & input::-webkit-outer-spin-button,
    & input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    & input[type='number'] {
      -moz-appearance: textfield; /* Firefox */
    }
  }
`;



const IconsImg = styled.img`
  height: 15px;
  width: 15px;
  object-fit: cover;
  margin-left: 10px;
`;

const GenerateListButton = styled.div`
  text-align: center;
  align-items: center;
  background-image: linear-gradient(
    90deg,
    ${COLOR.WheelEditColor1} 0%,
    ${COLOR.WheelEditColor2} 40%,
    ${COLOR.WheelEditColor3} 100%
  );
  color: #fff;
  display: flex;
  transition: all 300ms ease;
  white-space: nowrap;
  width: 90px;
  height: 30px;
  border-radius: 10px;
  justify-content: center;
  font-weight: bolder;
  cursor: pointer;
`;

const EditListOption = styled.div`
  text-align: center;
  align-items: center;
  background-image: linear-gradient(
    90deg,
    ${COLOR.WheelEditColor1} 0%,
    ${COLOR.WheelEditColor2} 40%,
    ${COLOR.WheelEditColor3} 100%
  );
  color: #fff;
  display: flex;
  transition: all 300ms ease;
  white-space: nowrap;
  width: 90px;
  height: 30px;
  border-radius: 10px;
  justify-content: center;
  font-weight: bolder;
  cursor: pointer;
`;

const ResetButton = styled.div`
  text-align: center;
  align-items: center;
  background: #d11919;
  color: #fff;
  display: flex;
  transition: all 300ms ease;
  white-space: nowrap;
  width: 100px;
  height: 30px;
  border-radius: 10px;
  justify-content: center;
  font-weight: bolder;
  cursor: pointer;
  // @media (max-width: 997px) {
  //   margin-top: 16px;
  // }
`;